import * as Api from '@ViewModels';
import { useQueries } from '@tanstack/react-query';
import * as React from 'react';
import { useDebounceValue } from '../../hooks/useDebounceValue';
import { useUserSession } from '../../models/hooks/appStateHooks';

const BASE_QUERY_KEY = 'social/instagram/userLookup';
const INSTAGRAM_USERNAME_REGEX = /^[A-Za-z0-9._]{1,30}$/;

export function useInstagramUsernameLookupQueries({
	usernames,
	enabled = true,
	forUserId,
	impersonationContext,
}: {
	usernames: string[];
	enabled?: boolean;
	forUserId?: string;
	impersonationContext?: Api.IImpersonationContext;
}) {
	const userSession = useUserSession();
	const effectiveImpersonationContext = Api.ImpersonationBroker.withForUser(
		impersonationContext || userSession.impersonationContext,
		forUserId
	);
	const accountId = effectiveImpersonationContext?.account?.id || userSession.account?.id;
	const userId = forUserId || effectiveImpersonationContext?.user?.id || userSession.user?.id;

	const lookupKey = React.useMemo(
		() =>
			Array.from(new Set(usernames.map(username => username.toLowerCase())))
				.filter(username => INSTAGRAM_USERNAME_REGEX.test(username))
				.sort()
				.join(','),
		[usernames]
	);
	const debouncedLookupKey = useDebounceValue(lookupKey, 500);
	const debouncedUsernames = React.useMemo(
		() => (debouncedLookupKey ? debouncedLookupKey.split(',') : []),
		[debouncedLookupKey]
	);

	const [hasLookupFailed, setHasLookupFailed] = React.useState(false);
	React.useEffect(() => setHasLookupFailed(false), [accountId, userId]);

	const results = useQueries({
		queries: debouncedUsernames.map(username => ({
			enabled: enabled && !hasLookupFailed,
			queryFn: () =>
				userSession.webServiceHelper.callAsync<Api.IInstagramUsernameLookupResult | null>(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: 'social/instagram/userLookup',
						queryParams: {
							forUserId,
							username,
						},
						impersonationContext: effectiveImpersonationContext,
					}),
					'GET'
				),
			queryKey: [BASE_QUERY_KEY, accountId, userId, username],
			retry: false,
			staleTime: 60 * 60 * 1000,
		})),
	});

	const hasFailedResult = results.some(result => result.isError);
	React.useEffect(() => {
		if (hasFailedResult) {
			setHasLookupFailed(true);
		}
	}, [hasFailedResult]);

	return React.useMemo(() => {
		const lookupsByUsername = new Map<string, Api.IInstagramUsernameLookupResult>();
		debouncedUsernames.forEach((username, index) => {
			const lookup = results[index]?.data;
			if (lookup) {
				lookupsByUsername.set(username, lookup);
			}
		});

		return lookupsByUsername;
	}, [debouncedUsernames, results]);
}
